import { Box, Column, Columns } from 'braid-design-system';
import { memo, useEffect, useMemo, useRef } from 'react';

import { useSplitViewContext } from 'src/handlers/SplitViewContext';
import useAuthenticated from 'src/hooks/useAuthenticated';
import {
  useBehaviouralCuesFiltersFeatureFlag,
  useFeatureFlagRefineBarV2Experiment,
} from 'src/store/featureFlags/hooks.ts';
import { selectSearchTag } from 'src/store/featureFlags/selectors';
import { useSelector } from 'src/store/react';
import { selectDynamicSearchBarType } from 'src/store/search/selectors';
import {
  selectIsResultsLoading,
  selectPaginationTotalPages,
  selectResults,
} from 'src/store/selectors';

import RelatedSearches from '../../RelatedSearches/RelatedSearches';
import { SearchResultsHeaderSpacer } from '../../SearchResultHeaders/SearchResultsHeaders';
import useSeoFooter, { type FilterList } from '../../SeoFooter/useSeoFooter';
import { MRECBanner } from '../MRECBanner/MRECBanner';

import { Footer, LIST_FOOTER_ITEM_SPACING } from './Footer';

import * as styles from './SplitViewLayout.css';

const outerSpacing = ['none', 'none', 'xxlarge'] as const;

// Props interface for RelatedSearches component
interface RelatedSearchesProps {
  filterInfo: FilterList;
}

// Memoized RelatedSearches component to prevent unnecessary re-renders
const MemoizedRelatedSearches = memo(({ filterInfo }: RelatedSearchesProps) => (
  <Box
    component="section"
    paddingTop={LIST_FOOTER_ITEM_SPACING}
    data-automation="related-searches-splitview"
    className={styles.relatedSearches}
  >
    <RelatedSearches filterInfo={filterInfo} />
  </Box>
));

MemoizedRelatedSearches.displayName = 'MemoizedRelatedSearches';

// Memoized Footer component
const MemoizedFooter = memo(() => <Footer />);
MemoizedFooter.displayName = 'MemoizedFooter';

interface SplitViewLayoutProps {
  ListView: React.ReactNode;
  DetailView: React.ReactNode;
}

const SplitViewLayout = memo(
  ({ ListView, DetailView }: SplitViewLayoutProps) => {
    const { selectedJob, isLoading } = useSplitViewContext();
    const shouldShowInitState = !Boolean(selectedJob);
    const { relatedList, shouldRenderRelatedSearch } = useSeoFooter();

    const totalPages = useSelector(selectPaginationTotalPages);
    const shouldHavePagination = totalPages > 1;
    const jobDetailsPaneElement = useRef<HTMLDivElement>(null);
    const { refineBarV2, stickySearchBar } =
      useFeatureFlagRefineBarV2Experiment();

    const dynamicSearchBarType = useSelector(selectDynamicSearchBarType);
    const isSearchLoading = useSelector(selectIsResultsLoading);
    const searchResultsViewModel = useSelector(selectResults);
    const { isZeroResults } = searchResultsViewModel || {};
    const hideResults = isSearchLoading || isZeroResults;

    const authenticateState = useAuthenticated();
    const isBehaviouralCuesFiltersEnabled =
      useBehaviouralCuesFiltersFeatureFlag();
    const isNewToYou = useSelector(selectSearchTag) === 'new';

    // Scroll job details pane to top when job changes or loading completes
    useEffect(() => {
      if (!isLoading && jobDetailsPaneElement.current?.scrollTo) {
        jobDetailsPaneElement.current.scrollTo({
          top: 0,
        });
      }
    }, [isLoading]);

    // Memoize the job details style and class names to prevent recalculation
    const jobDetailsStyles = useMemo(
      () => ({
        style: {
          top:
            (refineBarV2 || stickySearchBar) &&
            dynamicSearchBarType === 'minimised'
              ? 88
              : 0,
        },
        className: [
          {
            [styles.jobDetailsIndependentScrollSession]: !(
              refineBarV2 || stickySearchBar
            ),
            [styles.jobDetailsIndependentScrollSessionWithDynamicSearchBar]:
              refineBarV2 || stickySearchBar,
            [styles.makeScrollable]: !isLoading,
            [styles.disableScroll]: isLoading,
          },
        ],
      }),
      [refineBarV2, stickySearchBar, dynamicSearchBarType, isLoading],
    );

    // Memoize related searches component
    const relatedSearchesSection = useMemo(() => {
      if (hideResults || !shouldRenderRelatedSearch) return null;
      return <MemoizedRelatedSearches filterInfo={relatedList} />;
    }, [hideResults, shouldRenderRelatedSearch, relatedList]);

    return (
      <Box
        width="full"
        data-automation="splitViewParentWrapper"
        className={styles.splitViewWrapper}
      >
        <Columns collapseBelow="desktop" space={outerSpacing}>
          <Column width="2/5">
            <Box
              style={{ minHeight: 1400 }}
              position="relative"
              display="flex"
              flexDirection="column"
              paddingBottom={outerSpacing}
            >
              {relatedSearchesSection}
              <Box className={shouldHavePagination && styles.jobListWrapper}>
                {ListView}
              </Box>
              <Box width="full" flexGrow={1} />

              {hideResults ? null : <MemoizedFooter />}
              <Box
                display={hideResults ? 'none' : 'block'}
                className={styles.mrecBanner}
              >
                <MRECBanner visible={!hideResults} />
              </Box>
            </Box>
          </Column>
          <Column>
            <Box
              height="full"
              display={['none', 'none', 'block']}
              className={styles.jobDetailsWrapper}
            >
              <SearchResultsHeaderSpacer />
              <Box
                data-automation="splitViewJobDetailsWrapper"
                display={
                  authenticateState === 'unauthenticated' &&
                  isBehaviouralCuesFiltersEnabled &&
                  isNewToYou
                    ? 'none'
                    : 'block'
                }
                height="full"
                background="surface"
                marginBottom={shouldShowInitState ? outerSpacing : 'none'}
                className={jobDetailsStyles.className}
                style={jobDetailsStyles.style}
                ref={jobDetailsPaneElement}
                paddingBottom={outerSpacing}
              >
                {DetailView}
              </Box>
            </Box>
          </Column>
        </Columns>
      </Box>
    );
  },
);

SplitViewLayout.displayName = 'SplitViewLayout';

export default SplitViewLayout;
